import React, { useState, useMemo } from 'react';
import { Steps, InputNumber, Button, message, Progress, Typography, Descriptions, Statistic, Row, Col, Card, DatePicker, Tag, Checkbox } from 'antd';
import { CheckCircleTwoTone, CloseCircleOutlined, LoadingOutlined, SwapOutlined, CalendarOutlined } from '@ant-design/icons';
import ScheduleImport from './redistribution/ScheduleImport';
import ScatteredImport from './redistribution/ScatteredImport';
import RedistributionResult from './redistribution/RedistributionResult';
import { useGlobalContext } from '../../App';
import moment from 'moment';

const { Text } = Typography;

const steps = [
    { key: 1, title: '導入行程', description: '整周同事行程' },
    { key: 2, title: '導入散單', description: '未有固定同事的單' },
    { key: 3, title: '設置參數', description: '設定分配規則' },
    { key: 4, title: '運行分配', description: '執行重分配算法' },
    { key: 5, title: '查看結果', description: '查看分配方案' },
];

export default () => {
    const {
        scheduleData, setScheduleData,
        scatteredData, setScatteredData,
        redistributionResult, setRedistributionResult,
    } = useGlobalContext();

    const [current, setCurrent] = useState(0);
    const [minPcOrders, setMinPcOrders] = useState(25);
    const [maxFixtureCount, setMaxFixtureCount] = useState(80);
    const [loading, setLoading] = useState(false);
    const [percent, setPercent] = useState(0);
    const [status, setStatus] = useState(false);
    const [progressMsg, setProgressMsg] = useState('');
    const [successDone, setSuccessDone] = useState(false);

    // 日期篩選相關狀態
    const [selectedDates, setSelectedDates] = useState([]);    // 用戶勾選的日期列表

    const onChange = (value) => {
        setCurrent(value);
    };

    const next = () => {
        if (current === 0 && (!scheduleData || scheduleData.length === 0)) {
            message.warning('請先導入整周行程數據');
            return;
        }
        if (current === 1 && (!scatteredData || scatteredData.length === 0)) {
            message.warning('請先導入散單數據');
            return;
        }
        setCurrent(current + 1);
    };

    const prev = () => setCurrent(current - 1);

    // 行程和散單中出現的所有日期（合併去重排序）
    const allDates = useMemo(() => {
        const dateSet = new Set();
        [...(scheduleData || []), ...(scatteredData || [])].forEach(o => {
            if (o.service_date && o.service_date.trim()) dateSet.add(o.service_date.trim());
        });
        return [...dateSet].sort();
    }, [scheduleData, scatteredData]);

    // 各日期的行程數和散單數
    const dateStats = useMemo(() => {
        const stats = {};
        allDates.forEach(d => { stats[d] = { schedule: 0, scattered: 0, schedulePC: 0, scheduleHY: 0, scatteredPC: 0, scatteredHY: 0 }; });
        (scheduleData || []).forEach(o => {
            const d = o.service_date?.trim();
            if (d && stats[d]) {
                stats[d].schedule++;
                if (o.type === 'PC') stats[d].schedulePC++;
                if (o.type === 'HY') stats[d].scheduleHY++;
            }
        });
        (scatteredData || []).forEach(o => {
            const d = o.service_date?.trim();
            if (d && stats[d]) {
                stats[d].scattered++;
                if (o.type === 'PC') stats[d].scatteredPC++;
                if (o.type === 'HY') stats[d].scatteredHY++;
            }
        });
        return stats;
    }, [scheduleData, scatteredData, allDates]);

    // 當日期選擇變化時，自動全選/全不選
    const handleSelectAllDates = (checked) => {
        setSelectedDates(checked ? [...allDates] : []);
    };
    const handleToggleDate = (date, checked) => {
        setSelectedDates(prev => checked ? [...prev, date] : prev.filter(d => d !== date));
    };

    // 模擬進度條
    React.useEffect(() => {
        let timer;
        if (loading && percent <= 90) {
            timer = setInterval(() => {
                setPercent(p => Math.min(p + Math.floor(Math.random() * 10 + 1), 95));
            }, 1500);
        }
        return () => clearInterval(timer);
    }, [loading, percent]);

    const runRedistribution = async () => {
        setLoading(true);
        setProgressMsg('');
        setStatus(false);
        setPercent(0);
        setRedistributionResult(null);

        // 如果選了日期，只發送選中日期的數據
        const dateFilter = selectedDates.length > 0 ? selectedDates : null;
        const filteredSchedule = dateFilter
            ? scheduleData.filter(o => dateFilter.includes(o.service_date?.trim()))
            : scheduleData;
        const filteredScattered = dateFilter
            ? scatteredData.filter(o => dateFilter.includes(o.service_date?.trim()))
            : scatteredData;

        if (filteredSchedule.length === 0) {
            message.warning('選定日期範圍內沒有行程數據，請調整日期選擇');
            setLoading(false);
            return;
        }

        try {
            const params = {
                schedule_data: filteredSchedule,
                scattered_data: filteredScattered,
                min_pc_orders: minPcOrders,
                max_fixture_count: maxFixtureCount,
            };

            const res = await (
                await fetch('/api/runRedistribution', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(params),
                })
            ).json();

            setPercent(100);

            if (res.code === 200) {
                setRedistributionResult(res.data);
                setSuccessDone(true);
                setProgressMsg('重分配完成！在「查看結果」步驟中查看詳細方案。');
                setCurrent(4); // 跳到結果頁
            } else {
                setSuccessDone(false);
                const errMsg = res.msg || res.data?.error_message || '運行失敗';
                setProgressMsg(errMsg);
            }
        } catch (err) {
            setSuccessDone(false);
            setProgressMsg('網絡錯誤：' + err.message);
        }

        setStatus(true);
        setLoading(false);
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return <ScheduleImport />;
            case 1:
                return <ScatteredImport />;
            case 2:
                return (
                    <div style={{ padding: '24px 0' }}>
                        {/* 日期選擇 */}
                        <Card title={<span><CalendarOutlined style={{ marginRight: 8 }} />服務日期選擇</span>} style={{ marginBottom: 16 }}>
                            {allDates.length === 0 ? (
                                <div style={{ color: '#999' }}>暫無日期數據，請先導入行程或散單</div>
                            ) : (
                                <>
                                    <div style={{ marginBottom: 12 }}>
                                        <Checkbox
                                            checked={selectedDates.length === allDates.length}
                                            indeterminate={selectedDates.length > 0 && selectedDates.length < allDates.length}
                                            onChange={e => handleSelectAllDates(e.target.checked)}
                                        >
                                            全選 ({allDates.length} 個日期)
                                        </Checkbox>
                                        {selectedDates.length > 0 && (
                                            <Tag color="blue" style={{ marginLeft: 12 }}>
                                                已選 {selectedDates.length} 天
                                            </Tag>
                                        )}
                                    </div>
                                    <Row gutter={[8, 8]}>
                                        {allDates.map(date => {
                                            const s = dateStats[date] || {};
                                            const checked = selectedDates.includes(date);
                                            const weekday = moment(date, ['YYYY/MM/DD', 'YYYY-MM-DD']).format('ddd');
                                            return (
                                                <Col key={date} span={6}>
                                                    <Card
                                                        size="small"
                                                        style={{
                                                            cursor: 'pointer',
                                                            borderColor: checked ? '#1890ff' : '#d9d9d9',
                                                            background: checked ? '#e6f7ff' : '#fff',
                                                        }}
                                                        onClick={() => handleToggleDate(date, !checked)}
                                                    >
                                                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                                            <Checkbox checked={checked} style={{ marginRight: 6 }} />
                                                            {date} <span style={{ color: '#999', fontWeight: 400 }}>({weekday})</span>
                                                        </div>
                                                        <div style={{ fontSize: 12, color: '#666' }}>
                                                            行程 <Tag color="blue" style={{ marginRight: 4 }}>PC {s.schedulePC || 0}</Tag>
                                                            <Tag color="green" style={{ marginRight: 8 }}>HY {s.scheduleHY || 0}</Tag>
                                                            散單 <Tag color="blue" style={{ marginRight: 4 }}>PC {s.scatteredPC || 0}</Tag>
                                                            <Tag color="green">HY {s.scatteredHY || 0}</Tag>
                                                        </div>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </>
                            )}
                        </Card>

                        <Card title="分配規則設置" style={{ marginBottom: 16 }}>
                            <Row gutter={24}>
                                <Col span={12}>
                                    <Statistic
                                        title="PC同事最低PC單量"
                                        prefix={<SwapOutlined />}
                                        valueRender={() => (
                                            <InputNumber
                                                min={1}
                                                max={100}
                                                value={minPcOrders}
                                                onChange={setMinPcOrders}
                                                style={{ width: '100%' }}
                                            />
                                        )}
                                    />
                                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                                        每位PC同事期望單量不小於此數值
                                    </Text>
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title="PC+HY同事最大潔具數"
                                        prefix={<SwapOutlined />}
                                        valueRender={() => (
                                            <InputNumber
                                                min={1}
                                                max={200}
                                                value={maxFixtureCount}
                                                onChange={setMaxFixtureCount}
                                                style={{ width: '100%' }}
                                            />
                                        )}
                                    />
                                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                                        PC+HY同事如有HY單，總潔具總數量不可超過此值
                                    </Text>
                                </Col>
                            </Row>
                        </Card>

                        <Card title="數據預覽" size="small">
                            <Descriptions bordered column={2}>
                                <Descriptions.Item label="行程記錄數">{scheduleData?.length || 0}</Descriptions.Item>
                                <Descriptions.Item label="散單記錄數">{scatteredData?.length || 0}</Descriptions.Item>
                                <Descriptions.Item label="行程PC單">
                                    {scheduleData?.filter(o => o.type === 'PC').length || 0}
                                </Descriptions.Item>
                                <Descriptions.Item label="行程HY單">
                                    {scheduleData?.filter(o => o.type === 'HY').length || 0}
                                </Descriptions.Item>
                                <Descriptions.Item label="散單PC單">
                                    {scatteredData?.filter(o => o.type === 'PC').length || 0}
                                </Descriptions.Item>
                                <Descriptions.Item label="散單HY單">
                                    {scatteredData?.filter(o => o.type === 'HY').length || 0}
                                </Descriptions.Item>
                                {selectedDates.length > 0 && (
                                    <>
                                        <Descriptions.Item label="選中日期行程">
                                            {scheduleData?.filter(o => selectedDates.includes(o.service_date?.trim())).length || 0}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="選中日期散單">
                                            {scatteredData?.filter(o => selectedDates.includes(o.service_date?.trim())).length || 0}
                                        </Descriptions.Item>
                                    </>
                                )}
                            </Descriptions>
                        </Card>
                    </div>
                );
            case 3:
                return (
                    <div style={{ padding: '24px 0', textAlign: 'center' }}>
                        <Card>
                            <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
                                <Descriptions.Item label="行程記錄數">{scheduleData?.length || 0}</Descriptions.Item>
                                <Descriptions.Item label="散單記錄數">{scatteredData?.length || 0}</Descriptions.Item>
                                <Descriptions.Item label="選中服務日期">
                                    {selectedDates.length > 0
                                        ? selectedDates.map(d => <Tag key={d} color="blue" style={{ marginBottom: 4 }}>{d}</Tag>)
                                        : <span style={{ color: '#999' }}>未選擇（將使用全部日期）</span>
                                    }
                                </Descriptions.Item>
                                <Descriptions.Item label="選中日期行程數">
                                    {selectedDates.length > 0
                                        ? scheduleData?.filter(o => selectedDates.includes(o.service_date?.trim())).length || 0
                                        : scheduleData?.length || 0
                                    }
                                </Descriptions.Item>
                                <Descriptions.Item label="選中日期散單數">
                                    {selectedDates.length > 0
                                        ? scatteredData?.filter(o => selectedDates.includes(o.service_date?.trim())).length || 0
                                        : scatteredData?.length || 0
                                    }
                                </Descriptions.Item>
                                <Descriptions.Item label="PC同事最低PC單量">{minPcOrders}</Descriptions.Item>
                                <Descriptions.Item label="PC+HY同事最大潔具數">{maxFixtureCount}</Descriptions.Item>
                            </Descriptions>

                            <Button
                                type="primary"
                                size="large"
                                loading={loading}
                                onClick={runRedistribution}
                                style={{ marginTop: 16 }}
                            >
                                執行散單重分配
                            </Button>

                            {loading && (
                                <div style={{ marginTop: 24 }}>
                                    <Text><LoadingOutlined style={{ marginRight: 5 }} />運行中...</Text>
                                    <Progress percent={percent} style={{ marginTop: 8 }} />
                                </div>
                            )}

                            {status && (
                                <div style={{ marginTop: 24 }}>
                                    {successDone ? (
                                        <Text><CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 5 }} />{progressMsg}</Text>
                                    ) : (
                                        <Text><CloseCircleOutlined style={{ color: '#ff0000', marginRight: 5 }} />{progressMsg}</Text>
                                    )}
                                </div>
                            )}
                        </Card>
                    </div>
                );
            case 4:
                return <RedistributionResult />;
            default:
                return null;
        }
    };

    return (
        <div className="page">
            <h2><SwapOutlined style={{ marginRight: 8 }} />散單重分配</h2>
            <Steps current={current} items={steps} onChange={onChange} style={{ marginBottom: 24 }} />
            <div className="steps-content">{getStepContent(current)}</div>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
                {current > 0 && current < 4 && (
                    <Button style={{ margin: '0 8px' }} onClick={prev}>
                        上一步
                    </Button>
                )}
                {current < 3 && (
                    <Button type="primary" onClick={next}>
                        下一步
                    </Button>
                )}
            </div>
        </div>
    );
};
